import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AllMap from "./all-operators-map";
import OperatorPathMap from "./operator-path";
import PersonIcon from "@mui/icons-material/Person";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import GroupsIcon from "@mui/icons-material/Groups";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { useGetEmployeesTrackingList } from "src/api/employees";
import { useEffect, useMemo, useState } from "react";
import CustomAutocomplete from "src/shared/components/custom-auto-complete";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useGetFastifyLocationsList } from "src/api/locations";
import { NoData } from "src/shared/components/tables/no-data";

type employeeTypes = "outOfRange" | "active";

const TrackingOperatorsPage = () => {
  const [tempSearchBy, setTempSearchBy] = useState<string>("");
  const [searchBy, setSearchBy] = useState<string>("");
  const [selectedLocation, setSelectedLocation] = useState<any>();
  const [tempSelectedLocation, setTempSelectedLocation] = useState<any>();
  const [open, setOpen] = useState(false);
  const [tempType, setTempType] = useState<employeeTypes>("active");
  const [type, setType] = useState<employeeTypes>("active");
  const [clickedEmployeeID, setClickedEmployeeId] = useState("");

  // apis
  const { data: locationsData, isFetching } = useGetFastifyLocationsList({
    isOutOfRange: tempType === "outOfRange",
  });
  const { data } = useGetEmployeesTrackingList({
    outOfRange: type === "outOfRange",
    ...(searchBy ? { search: searchBy } : {}),
    ...(selectedLocation ? { locations: [selectedLocation.id] } : {}),
  });

  const handleSearch = (searchedVal: string) => {
    setTempSearchBy(searchedVal);
  };

  const clearSearch = () => {
    setTempSearchBy("");
  };

  const handleChangeLocation = (event: React.SyntheticEvent, newValue: any) => {
    setTempSelectedLocation(newValue);
  };

  const handleOpen = (id: string) => {
    setOpen(true);
    setClickedEmployeeId(id);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChangeType = (e: SelectChangeEvent) => {
    const value = e.target.value as employeeTypes;
    setTempType(value);
  };

  const handleApply = () => {
    setSelectedLocation(tempSelectedLocation);
    setSearchBy(tempSearchBy);
    setType(tempType);
  };

  const handleResetFilers = () => {
    setSelectedLocation(undefined);
    setSearchBy("");
    setType("outOfRange");

    setTempSelectedLocation(undefined);
    setTempSearchBy("");
    setTempType("outOfRange");
  };

  const sortedLocations = useMemo(() => {
    return (
      locationsData?.locations.sort((a: any, b: any) =>
        a.nameEn?.localeCompare(b.nameEn),
      ) ?? []
    );
  }, [locationsData]);

  useEffect(() => {
    setTempSelectedLocation(undefined);
  }, [tempType]);

  return (
    <Box p={"30px"} data-testid="tracking-operators-page">
      <Typography mb={{ xs: 2, md: 4 }} fontSize={"22px"} fontWeight={600}>
        Employees Tracking
      </Typography>
      <Stack direction={{ xs: "column", md: "row" }} gap={1} mb={2}>
        <Stack direction={"row"} gap={"10px"} flexWrap={"wrap"} flexGrow={1}>
          <TextField
            data-testid="tracking-search-input"
            size="small"
            placeholder="Search"
            sx={{ width: { xs: "100%", sm: "250px" } }}
            value={tempSearchBy}
            onChange={(event) => handleSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{ opacity: !tempSearchBy ? 0 : 1 }}
                >
                  <IconButton size="small" onClick={clearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box width={"230px"}>
            <CustomAutocomplete
              data-testid="tracking-location-input"
              size="small"
              fullWidth
              disablePortal
              id="combo-box-demo"
              key={tempSelectedLocation?.id ?? "location-autocomplete"}
              options={sortedLocations}
              getOptionLabel={(option: any) => option.nameEn}
              getOptionKey={(option: any) => option.id}
              value={tempSelectedLocation}
              onChange={handleChangeLocation}
              renderInput={(params: any) => (
                <TextField fullWidth {...params} label={"Location"} />
              )}
              loading={isFetching}
            />
          </Box>
          <FormControl size="small" sx={{ minWidth: "180px" }}>
            <InputLabel id="demo-simple-select-label">Type</InputLabel>
            <Select
              data-testid="tracking-type-input"
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={tempType}
              label="Type"
              onChange={handleChangeType}
            >
              {["outOfRange", "active"].map((typeValue: any) => (
                <MenuItem key={typeValue} value={typeValue}>
                  {typeValue === "outOfRange" ? "Out Of Location" : "Active"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Stack direction={"row"}>
          <Button
            onClick={handleResetFilers}
            data-testid="tracking-clear-filters-button"
          >
            Clear
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            sx={{ width: "100px" }}
            data-testid="tracking-apply-filters-button"
          >
            Apply
          </Button>
        </Stack>
      </Stack>
      {!data ? (
        <Box
          height={"400px"}
          display={"flex"}
          alignItems={"center"}
          justifyContent={"center"}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={1}>
            <Grid item xs={12} md={4}>
              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                border={"1px solid black"}
                paddingX={2}
                paddingY={2.5}
                gap={1}
                bgcolor={"#242424"}
                boxShadow={"0px 2.232px 11.161px 0px rgba(0, 0, 0, 0.10)"}
                height={"100%"}
              >
                <Stack direction={"row"} gap={1} alignItems={"center"}>
                  <GroupsIcon />
                  <Typography fontSize={"16px"}>Active Operators</Typography>
                </Stack>
                <Typography fontWeight={500}>
                  {data.meta.totalAvailable}
                </Typography>
              </Stack>
            </Grid>
            {/* <Grid item xs={12} md={4}>
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
            border={"1px solid black"}
            paddingX={2}
            paddingY={2.5}
            gap={1}
            bgcolor={"#242424"}
            boxShadow={"0px 2.232px 11.161px 0px rgba(0, 0, 0, 0.10)"}
          >
            <Stack direction={"row"} gap={1} alignItems={"center"}>
              <PauseCircleIcon />
              <Typography fontSize={"16px"}>Operators On Break</Typography>
            </Stack>
            <Typography fontWeight={500}>{data.meta.totalOutOfRange}</Typography>
          </Stack>
        </Grid> */}
            <Grid item xs={12} md={4}>
              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                border={"1px solid black"}
                paddingX={2}
                paddingY={2.5}
                gap={1}
                bgcolor={"#242424"}
                boxShadow={"0px 2.232px 11.161px 0px rgba(0, 0, 0, 0.10)"}
                height={"100%"}
              >
                <Stack direction={"row"} gap={1} alignItems={"center"}>
                  <ErrorIcon />
                  <Typography fontSize={"16px"}>
                    Operators out of location
                  </Typography>
                </Stack>
                <Typography fontWeight={500}>
                  {data.meta.totalOutOfRange}
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}></Grid>

            {data.data.length === 0 ? (
              <Box width={"100%"}>
                <NoData subtitle={"No Data Found For This Search"} />
              </Box>
            ) : (
              <>
                <Grid item xs={12} md={2.2}>
                  <Box maxHeight={"590px"} overflow={"auto"}>
                    {data.data.map((operator) => (
                      <Box
                        key={operator.id}
                        border={"1px solid gray"}
                        padding={1}
                        mb={2}
                        display={"flex"}
                        flexDirection={"column"}
                        gap={0.5}
                        borderRadius={"4px"}
                      >
                        <Stack direction={"row"} alignItems={"center"} gap={1}>
                          <PersonIcon fontSize="small" />
                          <Typography fontSize={"12px"}>
                            {operator.operatorName}
                          </Typography>
                        </Stack>
                        <Stack direction={"row"} alignItems={"center"} gap={1}>
                          <LocalPhoneIcon fontSize="small" />
                          <Typography fontSize={"12px"}>
                            {operator.operatorPhoneNumber ?? "_"}
                          </Typography>
                        </Stack>
                        <Stack direction={"row"} alignItems={"center"} gap={1}>
                          <LocationOnIcon fontSize="small" />
                          <Typography fontSize={"12px"}>
                            {operator.supportMode
                              ? "Support Mode"
                              : operator.locationName}
                          </Typography>
                        </Stack>
                        <Stack direction={"row"} justifyContent={"flex-end"}>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => handleOpen(operator.id)}
                          >
                            Track
                          </Button>
                        </Stack>
                      </Box>
                    ))}
                  </Box>
                </Grid>

                <Grid item xs={12} md={9.8} minHeight={"500px"}>
                  <AllMap list={data?.data} />
                </Grid>
              </>
            )}
          </Grid>
        </>
      )}

      {open && clickedEmployeeID && (
        <OperatorPathMap
          open={open}
          onClose={handleClose}
          id={clickedEmployeeID}
        />
      )}
    </Box>
  );
};

export default TrackingOperatorsPage;
